import { useCallback, useEffect, useState } from "react";
import { Button, Modal } from "react-bootstrap";

import { getCaseId, getClientId } from "@/Utils/helper";

import FaxDocumentPicker from "./FaxDocumentPicker";
import FaxPdfViewer from "./FaxPdfViewer";
import { finalizeFax, getFaxRecord, sendFax, startFax } from "./api/faxApi";

import "@/Components/DocumentModal/DocumentModal.css";
import "@/Components/AssembleDocument/assemble-document.css";
import "./fax-workflow.css";

const COVER_STEP = "cover";
const DOCUMENTS_STEP = "documents";
const REVIEW_STEP = "review";

const STEPS = [
  { id: COVER_STEP, label: "Cover Sheet" },
  { id: DOCUMENTS_STEP, label: "Select Documents" },
  { id: REVIEW_STEP, label: "Review & Send" },
];

const formatFaxNumber = (value) => {
  const digits = String(value || "").replace(/\D/g, "");
  if (digits.length === 10)
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  if (digits.length === 11 && digits.startsWith("1")) {
    return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }
  return digits || "No fax number";
};

const RecipientSummary = ({ record, fallbackName, fallbackFax }) => (
  <div className="fax-recipient-summary">
    <span>Fax recipient</span>
    <strong>{record?.recipient_name || fallbackName || "Contact"}</strong>
    <span>{formatFaxNumber(record?.fax_number || fallbackFax)}</span>
    <small>
      {record?.destination_email || "Fax destination will be generated by the provider."}
    </small>
  </div>
);

const FaxWorkflowModal = ({
  show,
  onClose,
  contactId,
  caseProviderId,
  recipientName,
  faxNumber,
  recipientPhone,
}) => {
  const clientId = getClientId();
  const caseId = getCaseId();
  const [activeStep, setActiveStep] = useState(COVER_STEP);
  const [record, setRecord] = useState(null);
  const [selectedDocIds, setSelectedDocIds] = useState([]);
  const [loadingDraft, setLoadingDraft] = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  const [sending, setSending] = useState(false);
  const [workflowError, setWorkflowError] = useState("");
  const [viewerError, setViewerError] = useState("");
  const [showSendConfirmation, setShowSendConfirmation] = useState(false);
  const [showSentConfirmation, setShowSentConfirmation] = useState(false);

  const beginFax = useCallback(async () => {
    setWorkflowError("");
    setViewerError("");
    setLoadingDraft(true);
    setRecord(null);
    setSelectedDocIds([]);
    setActiveStep(COVER_STEP);

    if (!clientId || !caseId || (!contactId && !caseProviderId)) {
      setWorkflowError("This contact is missing the case information required to start a fax.");
      setLoadingDraft(false);
      return;
    }

    const payload = {
      client_id: Number(clientId),
      case_id: Number(caseId),
    };
    if (caseProviderId) payload.case_provider_id = Number(caseProviderId);
    else payload.contact_id = Number(contactId);
    if (recipientName) payload.recipient_name = recipientName;
    if (faxNumber) payload.fax_number = faxNumber;
    if (recipientPhone) payload.recipient_phone = recipientPhone;

    try {
      const response = await startFax(payload);
      setRecord(response);
      setSelectedDocIds(response.doc_ids || []);
    } catch (error) {
      setWorkflowError(error.message);
    } finally {
      setLoadingDraft(false);
    }
  }, [caseId, caseProviderId, clientId, contactId, faxNumber, recipientName, recipientPhone]);

  useEffect(() => {
    if (show) beginFax();
  }, [beginFax, show]);

  const refreshRecord = async () => {
    if (!record?.id) return;
    setWorkflowError("");
    setViewerError("");
    try {
      const response = await getFaxRecord(record.id);
      setRecord(response);
      if (response.doc_ids?.length) setSelectedDocIds(response.doc_ids);
    } catch (error) {
      setWorkflowError(error.message);
    }
  };

  const handleFinalize = async () => {
    if (!record?.id || !selectedDocIds.length || finalizing) return;
    setWorkflowError("");
    setViewerError("");
    setFinalizing(true);
    try {
      const response = await finalizeFax(record.id, selectedDocIds);
      setRecord(response);
      setSelectedDocIds(response.doc_ids || selectedDocIds);
      setActiveStep(REVIEW_STEP);
    } catch (error) {
      setWorkflowError(error.message);
    } finally {
      setFinalizing(false);
    }
  };

  const handleSend = async () => {
    if (!record?.id || sending) return;
    setWorkflowError("");
    setSending(true);
    try {
      const response = await sendFax(record.id);
      setRecord(response);
      setShowSendConfirmation(false);
      setShowSentConfirmation(true);
      window.dispatchEvent(new CustomEvent("fax-sent", { detail: response }));
    } catch (error) {
      setShowSendConfirmation(false);
      setWorkflowError(error.message);
    } finally {
      setSending(false);
    }
  };

  const closeWorkflow = () => {
    if (sending) return;
    setShowSendConfirmation(false);
    setShowSentConfirmation(false);
    onClose();
  };

  const faxIsSent = record?.status === "sent";
  const activeStepIndex = STEPS.findIndex((step) => step.id === activeStep);

  return (
    <>
      <Modal
        show={show}
        onHide={closeWorkflow}
        size="xl"
        centered
        backdrop={sending ? "static" : true}
        keyboard={!sending}
        className="universal-document-modal custom-modal-width fax-workflow-modal"
        dialogClassName="document-modal-padding-setting"
      >
        <Modal.Header className="document-modal-header fax-workflow-modal-header height-25">
          <Modal.Title className="fax-workflow-modal-title">Fax Viewer</Modal.Title>
        </Modal.Header>

        <div className="fax-workflow-wizard m-t-5 m-b-0" aria-label="Fax workflow steps">
          {STEPS.map((step, index) => {
            const active = step.id === activeStep;
            const completed = index < activeStepIndex || faxIsSent;
            const unlocked =
              step.id === COVER_STEP ||
              (step.id === DOCUMENTS_STEP && Boolean(record)) ||
              (step.id === REVIEW_STEP && Boolean(record?.finalized_fax_url));
            return (
              <button
                type="button"
                key={step.id}
                disabled={!unlocked || loadingDraft || finalizing || sending}
                className={`fax-workflow-wizard-step ${active ? "active" : ""} ${
                  completed ? "completed" : ""
                }`}
                aria-current={active ? "step" : undefined}
                onClick={() => {
                  setWorkflowError("");
                  setViewerError("");
                  setActiveStep(step.id);
                }}
              >
                <span>{completed ? "✓" : `${index + 1}:`}</span>
                {step.label}
              </button>
            );
          })}
        </div>

        {record && (workflowError || viewerError) && (
          <div className="fax-workflow-alert is-error" role="alert">
            <span>{workflowError || viewerError}</span>
            {viewerError && record?.id && (
              <button type="button" onClick={refreshRecord}>
                Refresh Preview
              </button>
            )}
          </div>
        )}

        <Modal.Body className="document-modal-body fax-workflow-body">
          {loadingDraft ? (
            <div className="fax-workflow-loading" role="status">
              <span className="spinner-border" aria-hidden="true" />
              <strong>Preparing the fax cover sheet…</strong>
              <small>Recipient, firm, case, date, and sender variables are being filled.</small>
            </div>
          ) : !record ? (
            <div className="fax-workflow-loading is-error">
              <strong>The fax could not be started.</strong>
              <span>{workflowError}</span>
              <button type="button" className="btn btn-primary height-25" onClick={beginFax}>
                Retry
              </button>
            </div>
          ) : activeStep === COVER_STEP ? (
            <div className="fax-workflow-viewer-layout">
              <FaxPdfViewer
                file={record.cover_sheet_url}
                onLoadError={setViewerError}
                onRetry={refreshRecord}
              />
              <aside className="fax-workflow-sidebar">
                <div className="fax-workflow-sidebar-heading">
                  <strong>Prefilled Cover Sheet</strong>
                  <span>Review the generated first page before choosing attachments.</span>
                </div>
                <RecipientSummary
                  record={record}
                  fallbackName={recipientName}
                  fallbackFax={faxNumber}
                />

                <button
                  type="button"
                  className="btn btn-success height-25 d-flex align-items-center justify-content-center fax-workflow-primary-action"
                  onClick={() => {
                    setWorkflowError("");
                    setViewerError("");
                    setActiveStep(DOCUMENTS_STEP);
                  }}
                >
                  Select Documents to Fax
                </button>
              </aside>
            </div>
          ) : activeStep === DOCUMENTS_STEP ? (
            <div className="fax-workflow-documents-step">
              <FaxDocumentPicker
                caseId={caseId}
                selectedIds={selectedDocIds}
                onSelectionChange={(ids) => {
                  setSelectedDocIds(ids);
                  setWorkflowError("");
                }}
              />
              <div className="fax-workflow-step-footer">
                <button
                  type="button"
                  className="btn btn-secondary height-25"
                  disabled={finalizing}
                  onClick={() => setActiveStep(COVER_STEP)}
                >
                  Back to Cover Sheet
                </button>
                <div>
                  <button
                    type="button"
                    className={`btn height-25 ${selectedDocIds.length ? "btn-success" : "btn-success"}`}
                    disabled={!selectedDocIds.length || finalizing}
                    onClick={handleFinalize}
                  >
                    {finalizing ? "Review Final Fax" : "Review Final Fax"}
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="fax-workflow-viewer-layout">
              <FaxPdfViewer
                file={record.finalized_fax_url}
                onLoadError={setViewerError}
                onRetry={refreshRecord}
              />
              <aside className="fax-workflow-sidebar fax-send-sidebar">
                <div className="fax-workflow-sidebar-heading">
                  <strong>Review Final Fax</strong>
                  <span>
                    Review all {record.page_count || ""} pages before sending. The cover sheet is
                    first.
                  </span>
                </div>

                <button
                  type="button"
                  className={`fax-send-button ${faxIsSent ? "is-sent" : ""}`}
                  disabled={faxIsSent || sending || !record.sending_domain}
                  onClick={() => setShowSendConfirmation(true)}
                >
                  <span>{faxIsSent ? "Fax Sent" : "Send Fax to:"}</span>
                  <strong>{record.recipient_name || recipientName || "Contact"}</strong>
                  <span>{formatFaxNumber(record.fax_number || faxNumber)}</span>
                  <small>@{record.sending_domain || "not configured"}</small>
                </button>

                {!record.sending_domain && (
                  <div className="fax-workflow-info-card fax-provider-missing">
                    <span aria-hidden="true">!</span>
                    <p>
                      Choose an e-fax provider in{" "}
                      <a
                        href="/bp-firmsetting?tab_id=fax-settings"
                        target="_blank"
                        rel="noreferrer"
                      >
                        Firm Settings → Fax
                      </a>{" "}
                      before sending.
                    </p>
                  </div>
                )}

                {/* <dl className="fax-review-details">
                  <div>
                    <dt>Status</dt>
                    <dd className={`fax-status fax-status-${record.status}`}>{record.status}</dd>
                  </div>
                  <div>
                    <dt>Documents</dt>
                    <dd>{record.doc_ids?.length || selectedDocIds.length}</dd>
                  </div>
                  <div>
                    <dt>Total pages</dt>
                    <dd>{record.page_count || "—"}</dd>
                  </div>
                  <div>
                    <dt>Destination</dt>
                    <dd title={record.destination_email}>{record.destination_email || "—"}</dd>
                  </div>
                  {record.fax_doc_name && (
                    <div>
                      <dt>Saved file</dt>
                      <dd title={record.fax_doc_name}>{record.fax_doc_name}</dd>
                    </div>
                  )}
                </dl> */}

                {!faxIsSent && (
                  <button
                    type="button"
                    className="btn btn-outline-secondary d-flex align-items-center justify-content-center height-25 fax-review-change-documents"
                    onClick={() => setActiveStep(DOCUMENTS_STEP)}
                  >
                    Change Documents
                  </button>
                )}
              </aside>
            </div>
          )}
        </Modal.Body>
      </Modal>

      <Modal
        show={showSendConfirmation}
        onHide={() => !sending && setShowSendConfirmation(false)}
        centered
        backdrop={sending ? "static" : true}
        keyboard={!sending}
        className="fax-confirmation-modal"
      >
        <Modal.Header className="tfbp-modal-header">
          <Modal.Title
            className="tfbp-modal-title"
            style={{
              textTransform: "uppercase",
            }}
          >
            Confirm Fax
          </Modal.Title>
        </Modal.Header>
        <Modal.Body
          style={{
            padding: "5px",
          }}
        >
          <p className="m-b-5">Send this {record?.page_count || "finalized"}-page fax to:</p>
          <RecipientSummary record={record} fallbackName={recipientName} fallbackFax={faxNumber} />
        </Modal.Body>
        <Modal.Footer
          className="p-0 mt-0 padding-outside-btn-new-provider"
          style={{ borderTop: "none" }}
        >
          <Button
            className="button-padding-footer-new-provider d-flex align-items-center justify-content-center "
            variant="secondary"
            style={{
              fontWeight: "600",
            }}
            onClick={() => setShowSendConfirmation(false)}
          >
            Cancel
          </Button>
          <Button
            variant={"success"}
            style={{
              fontWeight: "600",
            }}
            className="button-padding-footer-new-provider d-flex align-items-center justify-content-center "
            disabled={sending}
            onClick={handleSend}
          >
            Send Fax
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showSentConfirmation}
        onHide={closeWorkflow}
        centered
        className="fax-confirmation-modal"
      >
        <Modal.Header className="tfbp-modal-header">
          <Modal.Title
            className="tfbp-modal-title"
            style={{
              textTransform: "uppercase",
            }}
          >
            Fax Sent
          </Modal.Title>
        </Modal.Header>

        <Modal.Body className="fax-sent-confirmation p-t-5 p-b-5 p-l-5 p-r-5">
          <div className="fax-sent-check" aria-hidden="true">
            ✓
          </div>
          <strong>The fax has been sent.</strong>
          <span>
            {record?.fax_doc_name
              ? `${record.fax_doc_name} was saved to the case.`
              : "The final PDF was saved to the case."}
          </span>
        </Modal.Body>
        <Modal.Footer
          className="p-0 mt-0 padding-outside-btn-new-provider"
          style={{ borderTop: "none" }}
        >
          <div />
          <Button
            className="button-padding-footer-new-provider d-flex align-items-center justify-content-center "
            variant="secondary"
            style={{
              fontWeight: "600",
            }}
            onClick={closeWorkflow}
          >
            Close
          </Button>
          <div />
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default FaxWorkflowModal;
